import { Link } from 'react-router-dom';
import {
  UXP_EH_ID,
  WORKSPACE_REGIONS,
  BRIEFING_TYPES,
  DEFAULT_HEALTH_SCORES,
  EXECUTIVE_KPIS,
  OPPORTUNITY_TYPES,
  RISK_TYPES,
  DEMO_OPPORTUNITIES,
  DEMO_RISKS,
  QUICK_ACTIONS,
  DEMO_TASKS,
  DEMO_TIMELINE,
  BRIEFING_SECTIONS,
  DEMO_DECISIONS,
  NOTIFICATION_CATEGORIES,
  WORKSPACE_HEALTH_METRICS,
  ANALYTICS_METRICS,
  LIVE_ROUTES,
  buildExecHomeDashboard,
  getTimeGreeting,
} from '../../exechome/framework';
import {
  WelcomeBanner,
  BusinessHealthGrid,
  ExecutiveKpiStrip,
  OpportunityRiskPanels,
  TaskCenterWidget,
  QuickActionsBar,
  ExecutiveTimelineWidget,
  WorkspaceHealthPanel,
} from '../../components/exechome';
import { ExecutiveBriefingCard, DecisionCenterPanel } from '../../components/rolexp';
import { Card, KpiCard } from '../../components/ui';

export function ExecutiveHomePage() {
  const dashboard = buildExecHomeDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_EH_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Executive Home Workspace</h1>
        <p className="mt-1 text-sm text-slate-500">
          AI-driven Business Command Center — personalized insights, priorities, recommendations, and decision support at the start of every workday.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-100 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Workspace Regions" value={dashboard.workspace_regions.toString()} />
        <KpiCard label="Health Dimensions" value={dashboard.health_dimensions.toString()} />
        <KpiCard label="Executive KPIs" value={dashboard.executive_kpis.toString()} />
        <KpiCard label="Quick Actions" value={dashboard.quick_actions.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Live Workspace Preview</h2>
        <p className="mt-1 text-sm text-slate-500">Composable executive home layout — UXP-00002 §2</p>
        <div className="mt-4 space-y-6">
          <WelcomeBanner greeting={getTimeGreeting()} userName="Chirag" />
          <div className="grid gap-6 lg:grid-cols-2">
            <ExecutiveBriefingCard greeting={`${getTimeGreeting()} — here's your commerce briefing`} sections={BRIEFING_SECTIONS} />
            <Card padding="sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Business Health Center</p>
              <div className="mt-3 max-h-64 overflow-y-auto">
                <BusinessHealthGrid scores={DEFAULT_HEALTH_SCORES.slice(0, 6)} />
              </div>
            </Card>
          </div>
          <ExecutiveKpiStrip kpis={EXECUTIVE_KPIS.slice(0, 8)} />
          <QuickActionsBar actions={QUICK_ACTIONS} />
          <OpportunityRiskPanels opportunities={DEMO_OPPORTUNITIES} risks={DEMO_RISKS} />
          <div className="grid gap-6 lg:grid-cols-2">
            <DecisionCenterPanel decisions={DEMO_DECISIONS} />
            <TaskCenterWidget tasks={DEMO_TASKS} />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <ExecutiveTimelineWidget events={DEMO_TIMELINE} />
            <WorkspaceHealthPanel metrics={WORKSPACE_HEALTH_METRICS} />
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Workspace Regions</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {WORKSPACE_REGIONS.map((r) => (
            <span key={r} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{r}</span>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Daily AI Briefing Types</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {BRIEFING_TYPES.map((b) => (
              <span key={b} className="rounded-lg border border-ai-200 bg-ai-50 px-2 py-1 text-xs text-ai-700 dark:border-ai-800 dark:bg-ai-950 dark:text-ai-300">{b}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Notification Categories</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {NOTIFICATION_CATEGORIES.map((n) => (
              <span key={n} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{n}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">AI Opportunity Types</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {OPPORTUNITY_TYPES.map((o) => (
              <span key={o} className="rounded-lg border border-success-200 bg-success-50 px-2 py-1 text-xs text-success-700 dark:border-success-800 dark:bg-success-950 dark:text-success-300">{o}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">AI Risk Types</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {RISK_TYPES.map((r) => (
              <span key={r} className="rounded-lg border border-error-200 bg-error-50 px-2 py-1 text-xs text-error-700 dark:border-error-800 dark:bg-error-950 dark:text-error-300">{r}</span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Full Business Health Grid</h2>
        <div className="mt-4">
          <BusinessHealthGrid scores={DEFAULT_HEALTH_SCORES} />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Analytics Metrics</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ANALYTICS_METRICS.map((m) => (
            <span key={m} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{m}</span>
          ))}
        </div>
      </section>

      <p className="text-xs text-slate-500">
        Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/uxp/UXP-00002_EXECUTIVE_HOME_WORKSPACE.md</code>
        {' · '}
        Registry: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/exechome/</code>
        {' · '}
        Briefing: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/copilot/briefing.go</code>
      </p>
    </div>
  );
}
